// Données métier centralisées — MS Énergie
import {
  Wind, Snowflake, Flame, Fan, Wrench, ShieldCheck, Clock, BadgeCheck,
  Gauge, Sparkles, ThermometerSun, Droplets,
} from "lucide-react";

export const COMPANY = {
  name: "MS Énergie",
  tagline: "Installer. Dépanner. Optimiser.",
  subtitle:
    "Votre spécialiste en climatisation, pompe à chaleur, chaudière et optimisation énergétique.",
  phone: "07 67 40 79 88",
  phoneRaw: "+33767407988",
  whatsapp: "33767407988",
  email: "ms-energie@outlook.fr",
  address: "7 rue Auguste Chevrollier",
  zip: "49800",
  city: "Trélazé",
  region: "Maine-et-Loire",
  area: "Angers & Maine-et-Loire (49)",
  hours: "Lun – Ven : 8h00 – 19h00",
  mapsQuery: "7+rue+Auguste+Chevrollier+49800+Trélazé",
};

// Lien WhatsApp robuste (fonctionne mobile + desktop)
export const waLink = (msg = "Bonjour MS Énergie, je souhaite obtenir des informations / un devis.") =>
  `https://api.whatsapp.com/send?phone=${COMPANY.whatsapp}&text=${encodeURIComponent(msg)}`;

// Lien email avec sujet pré-rempli
export const mailLink = (subject = "Demande d'information — Site MS Énergie") =>
  `mailto:${COMPANY.email}?subject=${encodeURIComponent(subject)}`;

export const SERVICES = [
  {
    slug: "climatisation",
    icon: Snowflake,
    title: "Climatisation",
    short: "Installation et entretien de systèmes de climatisation réversible.",
    desc: "Étude thermique, pose de splits muraux, gainables ou cassettes, mise en service et entretien régulier pour un confort optimal toute l'année.",
    points: ["Mono & multi-split", "Climatisation réversible", "Entretien annuel", "Mise en service certifiée"],
  },
  {
    slug: "pompe-a-chaleur",
    icon: Wind,
    title: "Pompe à chaleur",
    short: "PAC air/eau et air/air performantes et économiques.",
    desc: "Dimensionnement précis, installation de pompes à chaleur haute performance, raccordement hydraulique et accompagnement aux aides financières (MaPrimeRénov').",
    points: ["Air/Eau & Air/Air", "Économies d'énergie", "Aides & MaPrimeRénov'", "Maintenance"],
  },
  {
    slug: "chauffage",
    icon: Flame,
    title: "Chauffage & Chaudière",
    short: "Installation, dépannage et entretien de chaudières.",
    desc: "Remplacement de chaudières, installation de plancher chauffant et radiateurs, dépannage rapide et contrats d'entretien pour votre sécurité.",
    points: ["Chaudière gaz / condensation", "Plancher chauffant", "Dépannage rapide", "Contrat d'entretien"],
  },
  {
    slug: "ventilation",
    icon: Fan,
    title: "Ventilation (VMC)",
    short: "Solutions de ventilation pour un air sain et maîtrisé.",
    desc: "Installation de VMC simple et double flux, amélioration de la qualité de l'air intérieur et réduction de l'humidité dans votre logement.",
    points: ["VMC simple flux", "VMC double flux", "Qualité de l'air", "Anti-humidité"],
  },
];

export const VALUES = [
  { icon: Clock, title: "Intervention rapide", desc: "Une équipe réactive qui se déplace vite dans tout le Maine-et-Loire." },
  { icon: BadgeCheck, title: "Devis gratuit", desc: "Un chiffrage clair, détaillé et sans engagement avant toute intervention." },
  { icon: Sparkles, title: "Travail soigné", desc: "Finitions propres, matériel de qualité et chantier laissé impeccable." },
  { icon: Gauge, title: "Expertise technique", desc: "Des techniciens qualifiés et formés aux dernières technologies." },
  { icon: ShieldCheck, title: "Satisfaction client", desc: "Votre confort et votre confiance sont notre priorité absolue." },
];

export const EMERGENCY = [
  { icon: Snowflake, title: "Panne de climatisation", desc: "Climatiseur qui ne refroidit plus, fuit ou s'arrête." },
  { icon: Flame, title: "Chauffage en panne", desc: "Chaudière à l'arrêt, plus d'eau chaude ou radiateurs froids." },
  { icon: Wind, title: "Pompe à chaleur défaillante", desc: "PAC qui se met en sécurité ou perte de performance." },
  { icon: Droplets, title: "Fuite ou surconsommation", desc: "Fuite de fluide, bruit anormal ou facture qui explose." },
];

export const STEPS = [
  { n: "01", title: "Vous nous contactez", desc: "Par téléphone, WhatsApp ou via le formulaire de devis." },
  { n: "02", title: "Diagnostic & devis", desc: "Étude de votre besoin et chiffrage gratuit, clair et détaillé." },
  { n: "03", title: "Intervention", desc: "Installation ou dépannage par nos techniciens qualifiés." },
  { n: "04", title: "Suivi & entretien", desc: "Mise en service, conseils et contrat d'entretien optionnel." },
];

export const TESTIMONIALS = [
  { name: "Camille R.", city: "Angers", text: "Installation d'une pompe à chaleur impeccable. Équipe ponctuelle, propre et de très bon conseil. Je recommande vivement.", rating: 5 },
  { name: "Thierry L.", city: "Trélazé", text: "Dépannage de ma chaudière un samedi en plein hiver. Intervention rapide et efficace, un vrai professionnel.", rating: 5 },
  { name: "Sophie M.", city: "Avrillé", text: "Climatisation réversible posée dans toute la maison. Travail soigné et explications claires sur l'entretien.", rating: 5 },
  { name: "Julien P.", city: "Les Ponts-de-Cé", text: "Devis gratuit reçu très vite et tarif honnête. La VMC double flux fonctionne parfaitement. Merci !", rating: 5 },
];

// Réalisations — photos de démonstration (à remplacer par vos vraies photos)
export const PROJECTS = [
  {
    title: "Installation pompe à chaleur Air/Eau",
    city: "Angers",
    tag: "Pompe à chaleur",
    before: "https://images.unsplash.com/photo-1776860153678-b204dccd0f65?auto=format&fit=crop&w=1000&q=70",
    after: "https://images.unsplash.com/photo-1776860154052-d91c90b25c59?auto=format&fit=crop&w=1000&q=70",
  },
  {
    title: "Climatisation réversible multi-split",
    city: "Trélazé",
    tag: "Climatisation",
    before: "https://images.unsplash.com/photo-1694532438941-06bb0d95dae5?auto=format&fit=crop&w=1000&q=70",
    after: "https://images.unsplash.com/photo-1716846135834-ae0490d08c83?auto=format&fit=crop&w=1000&q=70",
  },
  {
    title: "Remplacement chaudière & entretien",
    city: "Avrillé",
    tag: "Chauffage",
    before: "https://images.pexels.com/photos/34938442/pexels-photo-34938442.jpeg?auto=compress&cs=tinysrgb&w=1000",
    after: "https://images.unsplash.com/photo-1518276779712-dfdcb9daa7a1?auto=format&fit=crop&w=1000&q=70",
  },
  {
    title: "Parc de pompes à chaleur collectif",
    city: "Les Ponts-de-Cé",
    tag: "Pompe à chaleur",
    before: "https://images.unsplash.com/photo-1667983453881-4992fe86ab1b?auto=format&fit=crop&w=1000&q=70",
    after: "https://images.unsplash.com/photo-1724488751821-1415f5cf4960?auto=format&fit=crop&w=1000&q=70",
  },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1761330439671-a7f20c285c5e?auto=format&fit=crop&w=1200&q=70",
  install: "https://images.unsplash.com/photo-1737012197886-7d5a52ded45b?auto=format&fit=crop&w=1000&q=70",
  maintenance: "https://images.unsplash.com/photo-1660330589827-da8ab7dd3c02?auto=format&fit=crop&w=1000&q=70",
  acTech: "https://images.unsplash.com/photo-1761330440311-16e160cad236?auto=format&fit=crop&w=1000&q=70",
  heatpump: "https://images.unsplash.com/photo-1776860150272-653efc74193c?auto=format&fit=crop&w=1000&q=70",
  chauffage: "https://images.pexels.com/photos/9551366/pexels-photo-9551366.jpeg?auto=compress&cs=tinysrgb&w=1000",
  ventilation: "https://images.unsplash.com/photo-1708705261211-b2a244a811dc?auto=format&fit=crop&w=1000&q=70",
};

export const SERVICE_OPTIONS = [
  "Climatisation",
  "Pompe à chaleur",
  "Chauffage / Chaudière",
  "Ventilation (VMC)",
  "Dépannage d'urgence",
  "Entretien / Maintenance",
  "Autre / Conseil",
];

export const EQUIPMENT_OPTIONS = [
  "Pompe à chaleur",
  "Climatisation",
  "Chaudière",
  "Chauffe-eau / Ballon",
  "Ventilation (VMC)",
  "Plancher chauffant",
  "Autre / Je ne sais pas",
];

export const BREAKDOWN_OPTIONS = [
  "Machine à l'arrêt",
  "Fuite (eau / fluide)",
  "Code erreur affiché",
  "Plus de chauffage",
  "Plus de froid / climatisation",
  "Plus d'eau chaude",
  "Bruit anormal",
  "Mauvaise odeur",
  "Surconsommation / facture élevée",
  "Autre panne",
];
